import { Component, OnInit, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { FirestoreDataService } from '../core/firestore-data.service';
import { WorkshopWork, WorkStatus, Client } from '../core/models';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';

declare var Chart: any;

@Component({
  selector: 'app-analytics',
  templateUrl: './analytics.component.html',
  styleUrls: ['./analytics.component.scss']
})
export class AnalyticsComponent implements OnInit, AfterViewInit {
  @ViewChild('revenueChart') revenueChartCanvas!: ElementRef;
  @ViewChild('intakeChart') intakeChartCanvas!: ElementRef;

  isLoading = true;
  
  // Data cruda en caché para no saturar Firebase
  allWorks: WorkshopWork[] = [];
  allStatuses: WorkStatus[] = [];
  allClients: Client[] = [];

  // Filtro de Tiempo
  selectedPeriod: 'day' | 'week' | 'month' | 'quarter' | 'year' | 'all' = 'month';

  // KPIs Monetarios
  totalRevenue = 0;
  avgTicket = 0;
  totalJobs = 0;

  // Eficiencia Taller (Recibido -> Ready)
  avgDaysToCompletePeriod = 0;
  avgDaysToCompleteHistory = 0;

  // Efectividad Taller (% entregado en <= 3 días)
  effectivenessPeriod = 0;
  effectivenessHistory = 0;
  private readonly SLA_DAYS = 3; // Meta de días para completar un trabajo

  effectiveJobsPeriodCount = 0;
  compCountPeriodCount = 0;
  effectiveJobsHistoryCount = 0;
  compCountHistoryCount = 0;

  // KPIs de Clientes
  newClientsPeriod = 0;
  newClientsHistory = 0;
  
  // Listas de Retención (Datos procesados)
  inactiveClientsList: { client: Client, days: number, date: Date }[] = [];
  noFullServiceClientsList: { client: Client, days: number | null, date: Date | null }[] = [];

  topClientsByRevenue: any[] = [];
  topClientsByVolume: any[] = []; 
  revenueByServiceType: any = {};

  // Instancias de Gráficas para poder destruirlas y redibujarlas
  private intakeChartInstance: any;
  private revenueChartInstance: any;

  constructor(private firestoreDataService: FirestoreDataService) {}

  ngOnInit(): void {
    this.loadData();
  }

  ngAfterViewInit(): void {}

  private loadData() {
    combineLatest([
      this.firestoreDataService.getAllWorksForAnalytics(),
      this.firestoreDataService.getAllStatusesForAnalytics(),
      this.firestoreDataService.getClients()
    ]).pipe(take(1)).subscribe(([works, statuses, clients]) => {
      this.allWorks = works;
      this.allStatuses = statuses;
      this.allClients = clients;
      
      this.calculateHistoricalBaselines(); // Bases históricas para comparativas
      this.calculateRetentionLists();      // Listas de clientes (siempre analizan toda la historia)
      this.applyPeriodFilter(this.selectedPeriod); // Calcular KPIs de periodo y dibujar charts

      this.isLoading = false;
    });
  }

  setPeriod(period: 'day' | 'week' | 'month' | 'quarter' | 'year' | 'all') {
    this.selectedPeriod = period;
    this.applyPeriodFilter(period);
  }

  private applyPeriodFilter(period: string) {
    const now = new Date();
    let startDate = new Date();
    startDate.setHours(0,0,0,0);

    if (period === 'week') startDate.setDate(now.getDate() - now.getDay());
    else if (period === 'month') startDate.setDate(1);
    else if (period === 'quarter') startDate.setMonth(Math.floor(now.getMonth() / 3) * 3, 1);
    else if (period === 'year') { startDate.setMonth(0, 1); }
    else if (period === 'all') startDate = new Date(2000, 0, 1);

    // Filtrar trabajos del periodo
    const periodWorks = this.allWorks.filter(w => {
      const eDate = w.entryDate?.toDate ? w.entryDate.toDate() : new Date(w.entryDate);
      return eDate >= startDate && eDate <= now && w.currentStatus !== 'Cancelled';
    });

    this.processPeriodKPIs(periodWorks, startDate);
    this.initCharts(periodWorks);
  }

  private processPeriodKPIs(periodWorks: WorkshopWork[], startDate: Date) {
    this.totalRevenue = 0;
    this.totalJobs = periodWorks.length;
    this.revenueByServiceType = {};

    let compCountPeriod = 0;
    let sumCompPeriod = 0;
    let effectiveJobsPeriod = 0;

    periodWorks.forEach(work => {
      this.totalRevenue += work.totalPrice;

      // Agregación de Ingresos por tipo
      const sType = work.serviceType || 'Otros';
      this.revenueByServiceType[sType] = (this.revenueByServiceType[sType] || 0) + work.totalPrice;

      // Eficiencia y Efectividad
      const workStatus = this.allStatuses.find(s => s.workId === work.id);
      if (workStatus?.history) {
        const rEntry = workStatus.history.find(h => h.status === 'Received');
        const rdEntry = workStatus.history.find(h => h.status === 'Ready');
        
        if (rEntry && rdEntry) {
          const days = this.calculateWorkDays(rEntry.timestamp.toDate(), rdEntry.timestamp.toDate());
          sumCompPeriod += days; 
          compCountPeriod++;
          if (days <= this.SLA_DAYS) effectiveJobsPeriod++;
        }
      }
    });

    this.avgTicket = this.totalJobs > 0 ? this.totalRevenue / this.totalJobs : 0;
    this.avgDaysToCompletePeriod = compCountPeriod > 0 ? sumCompPeriod / compCountPeriod : 0;
    this.effectivenessPeriod = compCountPeriod > 0 ? (effectiveJobsPeriod / compCountPeriod) * 100 : 0;

     this.effectiveJobsPeriodCount = effectiveJobsPeriod;
    this.compCountPeriodCount = compCountPeriod;

    // Clientes Nuevos en este periodo
    this.newClientsPeriod = this.allClients.filter(c => {
      const createdAt = c.createdAt?.toDate ? c.createdAt.toDate() : new Date(c.createdAt);
      return createdAt >= startDate;
    }).length;
  }

  private calculateHistoricalBaselines() {
    let compCountHist = 0;
    let sumCompHist = 0;
    let effectiveJobsHist = 0;

    const clientStatsMap = new Map<string, { name: string, volume: number, revenue: number }>();

    this.allWorks.forEach(work => {
      if (work.currentStatus === 'Cancelled') return;

      const stats = clientStatsMap.get(work.clientId) || { name: work.clientName, volume: 0, revenue: 0 };
      stats.volume++;
      stats.revenue += work.totalPrice;
      clientStatsMap.set(work.clientId, stats);

      const workStatus = this.allStatuses.find(s => s.workId === work.id);
      if (workStatus?.history) {
        const rEntry = workStatus.history.find(h => h.status === 'Received');
        const rdEntry = workStatus.history.find(h => h.status === 'Ready');
        if (rEntry && rdEntry) {
          const days = this.calculateWorkDays(rEntry.timestamp.toDate(), rdEntry.timestamp.toDate());
          sumCompHist += days; 
          compCountHist++;
          if (days <= this.SLA_DAYS) effectiveJobsHist++;
        }
      }
    });

    this.avgDaysToCompleteHistory = compCountHist > 0 ? sumCompHist / compCountHist : 0;
    this.effectivenessHistory = compCountHist > 0 ? (effectiveJobsHist / compCountHist) * 100 : 0;

    this.effectiveJobsHistoryCount = effectiveJobsHist;
    this.compCountHistoryCount = compCountHist;

    const allClientStats = Array.from(clientStatsMap.values());
    this.topClientsByRevenue = [...allClientStats].sort((a, b) => b.revenue - a.revenue).slice(0, 5);
    this.topClientsByVolume = [...allClientStats].sort((a, b) => b.volume - a.volume).slice(0, 5);
  }

  private calculateRetentionLists() {
    const now = new Date();
    const clientVisitMap = new Map<string, Date>();
    const clientLastFullMap = new Map<string, Date>();

    this.allWorks.forEach(work => {
      if (work.currentStatus === 'Cancelled') return;
      const entryDate = work.entryDate?.toDate ? work.entryDate.toDate() : new Date(work.entryDate);

      if (!clientVisitMap.has(work.clientId) || entryDate > clientVisitMap.get(work.clientId)!) {
        clientVisitMap.set(work.clientId, entryDate);
      }

      if (work.serviceType === 'Full') {
        if (!clientLastFullMap.has(work.clientId) || entryDate > clientLastFullMap.get(work.clientId)!) {
          clientLastFullMap.set(work.clientId, entryDate);
        }
      }
    });

    this.inactiveClientsList = [];
    this.noFullServiceClientsList = [];

    this.allClients.forEach(c => {
      // Inactivos > 90 días
      const lastVisit = clientVisitMap.get(c.id!);
      if (lastVisit) {
        const diffDays = Math.ceil(Math.abs(now.getTime() - lastVisit.getTime()) / (1000 * 60 * 60 * 24));
        if (diffDays > 90) {
          this.inactiveClientsList.push({ client: c, days: diffDays, date: lastVisit });
        }
      }

      // Nunca Full o > 60 días
      const lastFull = clientLastFullMap.get(c.id!);
      if (!lastFull) {
        this.noFullServiceClientsList.push({ client: c, days: null, date: null });
      } else {
        const diffDays = Math.ceil(Math.abs(now.getTime() - lastFull.getTime()) / (1000 * 60 * 60 * 24));
        if (diffDays > 60) {
          this.noFullServiceClientsList.push({ client: c, days: diffDays, date: lastFull });
        }
      }
    });

    // Ordenar listas para mostrar los casos más críticos primero
    this.inactiveClientsList.sort((a, b) => b.days - a.days);
    this.noFullServiceClientsList.sort((a, b) => (b.days || 9999) - (a.days || 9999));
  }

  private calculateWorkDays(start: Date, end: Date): number {
    if (end < start) return 0;
    let count = 0;
    let cur = new Date(start);
    while (cur <= end) {
      if (cur.getDay() !== 0) count++; // Ignorar Domingos
      cur.setDate(cur.getDate() + 1);
    }
    return count === 0 ? 0.5 : count;
  }

  // --- ACCIONES DE MARKETING ---
  sendWhatsapp(client: Client, type: 'inactivo' | 'no-full', days: number | null) {
    if (!client.phone) { alert("Este cliente no tiene teléfono registrado."); return; }
    
    let msg = '';
    if (type === 'inactivo') {
      msg = `¡Hola ${client.name}! Te extrañamos en ServiTrack. Notamos que hace ${days} días no nos visitas. ¿Todo bien con tu máquina? Avísanos si necesitas agendar una revisión.`;
    } else {
      const timeRef = days ? `hace ${days} días` : 'recientemente';
      msg = `¡Hola ${client.name}! Queríamos recordarte que un servicio Full a tiempo alarga la vida de tus suspensiones y transmisión. Notamos que no le has dado un cariño profundo a tu bici ${timeRef}. ¡Agenda con nosotros!`;
    }
    
    const phone = client.phone.replace(/\D/g, ''); // Limpiar formato
    window.open(`https://wa.me/1${phone}?text=${encodeURIComponent(msg)}`, '_blank');
  }

  sendMassEmail(type: 'inactivo' | 'no-full') {
    const list = type === 'inactivo' ? this.inactiveClientsList : this.noFullServiceClientsList;
    const emails = list.map(item => item.client.email).filter(e => e && e.includes('@'));
    
    if (emails.length === 0) {
      alert("No hay correos electrónicos válidos en esta lista.");
      return;
    }

    const bcc = emails.join(',');
    const subject = type === 'inactivo' ? '¡Te extrañamos en ServiTrack!' : '¡Es hora de consentir tu bicicleta!';
    const body = type === 'inactivo' 
      ? 'Hola, notamos que hace un tiempo no nos visitas. ¡Recuerda que mantener tu bici a punto es vital!'
      : 'Hola, un Servicio Full a tiempo ahorra dinero en piezas rotas. ¡Agenda tu cita hoy!';

    window.location.href = `mailto:?bcc=${bcc}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  // --- GRÁFICAS ---
  private initCharts(works: WorkshopWork[]) {
    setTimeout(() => {
      this.renderIntakeChart(works);
      this.renderRevenueChart();
    }, 100);
  }

  private renderIntakeChart(works: WorkshopWork[]) {
    if (this.intakeChartInstance) this.intakeChartInstance.destroy();

    // Determinar agrupación (Por mes si es 'all' o 'year', por día si es menor)
    const labels = [];
    const counts = [];
    
    if (this.selectedPeriod === 'all' || this.selectedPeriod === 'year' || this.selectedPeriod === 'quarter') {
      const today = new Date();
      for (let i = 5; i >= 0; i--) {
        const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
        labels.push(d.toLocaleString('es-ES', { month: 'short', year: '2-digit' }));
        counts.push(works.filter(w => {
          const eDate = w.entryDate?.toDate ? w.entryDate.toDate() : new Date(w.entryDate);
          return eDate.getMonth() === d.getMonth() && eDate.getFullYear() === d.getFullYear();
        }).length);
      }
    } else {
      // Agrupación por días para periodos cortos
      const today = new Date();
      for (let i = 6; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        labels.push(d.toLocaleString('es-ES', { weekday: 'short', day: '2-digit' }));
        counts.push(works.filter(w => {
          const eDate = w.entryDate?.toDate ? w.entryDate.toDate() : new Date(w.entryDate);
          return eDate.getDate() === d.getDate() && eDate.getMonth() === d.getMonth();
        }).length);
      }
    }

    this.intakeChartInstance = new Chart(this.intakeChartCanvas.nativeElement, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [{
          label: 'Órdenes', data: counts, borderColor: '#0d6efd',
          backgroundColor: 'rgba(13, 110, 253, 0.1)', fill: true, tension: 0.4
        }]
      },
      options: { responsive: true, maintainAspectRatio: false }
    });
  }

  private renderRevenueChart() {
    if (this.revenueChartInstance) this.revenueChartInstance.destroy();

    this.revenueChartInstance = new Chart(this.revenueChartCanvas.nativeElement, {
      type: 'doughnut',
      data: {
        labels: Object.keys(this.revenueByServiceType),
        datasets: [{
          data: Object.values(this.revenueByServiceType),
          backgroundColor: ['#0d6efd', '#198754', '#0dcaf0', '#ffc107', '#6c757d']
        }]
      },
      options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }
    });
  }
}